import React, { useId } from 'react';

// Dropdown icons for the hero request bar, drawn in the house glyph style:
// solid black silhouettes with fine details cut in (like the jet, car and boat),
// on a 24px grid so they sit neatly beside each option label.

type GlyphProps = {
  className?: string;
};
type Glyph = (p: GlyphProps) => JSX.Element;
type SolidProps = GlyphProps & {
  children: React.ReactNode; // the solid silhouette
  cuts?: React.ReactNode; // details cut out of it (draw in black; white restores)
  extra?: React.ReactNode; // uncut accents drawn on top
};
export function Solid({
  className,
  children,
  cuts,
  extra
}: SolidProps) {
  const id = useId().replace(/:/g, '');
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-699" data-mp="vf54p1i0h6ek">
      {cuts && <defs data-id="element-700" data-mp="3k6zcsc593mtd">
          <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24" data-id="element-701" data-mp="3k6pnyrvxzlp9">
            <rect width="24" height="24" fill="white" data-id="element-702" data-mp="3g5dsw6uau89o" />
            <g fill="black" data-id="element-703" data-mp="3g3sych7zipbt">{cuts}</g>
          </mask>
        </defs>}
      <g fill="currentColor" mask={cuts ? `url(#${id})` : undefined} data-id="element-704" data-mp="3rn0rg8cy37qe">
        {children}
      </g>
      {extra}
    </svg>;
}

// A fine line, cut into the silhouette
export const Cut = ({
  d,
  w = 1.1
}: {
  d: string;
  w?: number;
}) => <path d={d} fill="none" stroke="black" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" data-id="element-705" data-mp="3imcihs2erxkd" />;
// A fine line, drawn in the icon colour
export const Stroke = ({
  d,
  w = 1.2,
  opacity
}: {
  d: string;
  w?: number;
  opacity?: number;
}) => <path d={d} fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" opacity={opacity} data-id="element-706" data-mp="3do16pbsjzczi" />;

/* ——— Destinations ——— */

const Dubai: Glyph = ({
  className
}) =>
// Burj Khalifa — stepped, tapering spire
<Solid className={className} cuts={<Cut d="M12 8.6V20.4" w={0.7} data-id="element-708" data-mp="2py6w70vpi0if" />} extra={<Stroke d="M3 22.4H21" w={1} opacity={0.5} data-id="element-709" data-mp="2pz9n38t142yc" />} data-id="element-707" data-mp="33xzhuhsdlqjj">
    <path d="M11.7 1H12.3L12.6 5.8L13.2 9L13.7 12.8L14.8 15L15.3 21.6H8.7L9.2 15L10.3 12.8L10.8 9L11.4 5.8Z" data-id="element-710" data-mp="2uwrugureea48" />
  </Solid>;
const Monaco: Glyph = ({
  className
}) =>
// The Casino — domed pavilion with pennant and arched windows
<Solid className={className} cuts={<>
        <path d="M6.6 20V16.2A1.1 1.1 0 0 1 8.8 16.2V20Z" data-id="element-712" data-mp="qqzxkq5trv63" />
        <path d="M10.9 20V16.2A1.1 1.1 0 0 1 13.1 16.2V20Z" data-id="element-713" data-mp="qrjga93aunl6" />
        <path d="M15.2 20V16.2A1.1 1.1 0 0 1 17.4 16.2V20Z" data-id="element-714" data-mp="mq7l7o1npa5l" />
        <Cut d="M4.6 13.6H19.4" w={0.8} data-id="element-715" data-mp="mnletrqi50oy" />
      </>} extra={<>
        <Stroke d="M12 2.6V7" w={1} data-id="element-716" data-mp="1jevzqae5yfa9" />
        <path d="M12 2.6L15.2 3.6L12 4.6Z" fill="currentColor" data-id="element-717" data-mp="1egu7ozyrf5mn" />
      </>} data-id="element-711" data-mp="vp1n9txrasqj">
    <path d="M7 12A5 5 0 0 1 17 12Z" data-id="element-718" data-mp="1eerksoswlsm5" />
    <path d="M4 12.4H20V21H4Z" data-id="element-719" data-mp="1ae7lbekuycwu" />
    <path d="M2.6 21.4H21.4V22.6H2.6Z" data-id="element-720" data-mp="1ad5xsamnnxy8" />
  </Solid>;
const Ibiza: Glyph = ({
  className
}) =>
// Es Vedrà rising from the sea, sun setting behind
<Solid className={className} cuts={<Cut d="M8.4 9.4L9.8 11.6" w={0.8} data-id="element-722" data-mp="3v6swjbhrgfbv" />} extra={<>
        <Stroke d="M2 19.4Q4 18.2 6 19.4T10 19.4T14 19.4T18 19.4T22 19.4" w={1.1} data-id="element-723" data-mp="3q5vgp5k83xdj" />
        <Stroke d="M5 22Q7 21 9 22T13 22T17 22" w={1} opacity={0.5} data-id="element-724" data-mp="25ocjrx6fqspg" />
      </>} data-id="element-721" data-mp="3v5r9e8nh02ds">
    <circle cx="16.4" cy="8.4" r="3.2" data-id="element-725" data-mp="2an71evso2b7z" />
    <path d="M2.6 17.2L8.2 6L11.2 10L13.6 13.6L15.8 17.2Z" data-id="element-726" data-mp="2ao8ndvb80i4h" />
  </Solid>;
const Hamburg: Glyph = ({
  className
}) =>
// Elbphilharmonie — glass wave over the brick warehouse
<Solid className={className} cuts={<>
        <Cut d="M5.4 15.4H18.6" w={0.9} data-id="element-728" data-mp="2jhlqfq30104a" />
        <Cut d="M4.8 10.2H19.2" w={0.7} data-id="element-729" data-mp="2of3rkuaryb1q" />
      </>} extra={<Stroke d="M2 21Q4 20 6 21T10 21T14 21T18 21T22 21" w={1} opacity={0.6} data-id="element-730" data-mp="2ogyay46edv3p" />} data-id="element-727" data-mp="2jg0vw0goph6f">
    <path d="M3 12V8.4Q5.5 4.6 7.6 7.8Q9.8 4.2 12 7.6Q14.2 4.6 16.4 7.8Q18.6 6.2 21 7.4V12Z" data-id="element-731" data-mp="2tbu4x9v72sjg" />
    <path d="M3 12.8H21V18.4H3Z" data-id="element-732" data-mp="2ydjgqrq8ra8l" />
  </Solid>;
const Mallorca: Glyph = ({
  className
}) =>
// A sailing yacht on calm water
<Solid className={className} cuts={<Cut d="M6 18.2H18" w={0.7} data-id="element-734" data-mp="13zsbe4ey3ciy" />} extra={<Stroke d="M3 22Q5.25 21 7.5 22T12 22T16.5 22T21 22" w={1} opacity={0.5} data-id="element-735" data-mp="140toeefiu93b" />} data-id="element-733" data-mp="32c0mp7xxeeof">
    <path d="M11.4 2.4L18.2 15H11.4Z" data-id="element-736" data-mp="180lyivkkcnas" />
    <path d="M10.4 4.8L4.8 15H10.4Z" data-id="element-737" data-mp="cpvuyztzjkpl" />
    <path d="M10.4 15.4H11.4V16.4H10.4Z" data-id="element-738" data-mp="couj4t55au6t" />
    <path d="M2.8 16.4H21.2L18.8 20.2H5.2Z" data-id="element-739" data-mp="cs0akf18y85p" />
  </Solid>;
const Warsaw: Glyph = ({
  className
}) =>
// Palace of Culture and Science — tiered tower with wings
<Solid className={className} cuts={<>
        <Cut d="M10.8 13V20.4M13.2 13V20.4" w={0.8} data-id="element-741" data-mp="hoqnithr8nmw" />
        <Cut d="M5.6 17.4V20.4M18.4 17.4V20.4" w={0.8} data-id="element-742" data-mp="hq1oymnq9jau" />
      </>} data-id="element-740" data-mp="mn0gqyjx5e5v">
    <path d="M11.6 1H12.4V3.2H13.1V6.6H14.1V10.6H15.7V21.6H8.3V10.6H9.9V6.6H10.9V3.2H11.6Z" data-id="element-743" data-mp="3q63hysa9ry2k" />
    <path d="M3.4 21.6V15.4H8V21.6Z" data-id="element-744" data-mp="3l70db8y7btv4" />
    <path d="M16 15.4H20.6V21.6H16Z" data-id="element-745" data-mp="3l3w4fy9sk9y4" />
    <path d="M2 21.8H22V22.8H2Z" data-id="element-746" data-mp="3pze3kdu7q53" />
  </Solid>;
const Menorca: Glyph = ({
  className
}) =>
// Lighthouse on the point, beams out to sea
<Solid className={className} cuts={<>
        <path d="M9.3 15.2H14.7L14.85 16.6H9.15Z" data-id="element-748" data-mp="37e0yl968u3om" />
        <path d="M11.2 6.1H12.8V7.5H11.2Z" data-id="element-749" data-mp="37cpu174z4rwg" />
      </>} extra={<>
        <Stroke d="M15.6 5.4L19.4 4.2M15.6 7.4L19.4 8.6M8.4 5.4L4.6 4.2M8.4 7.4L4.6 8.6" w={1} data-id="element-750" data-mp="32ey6sqf9xtyl" />
        <Stroke d="M4 22.2H20" w={1} opacity={0.5} data-id="element-751" data-mp="1hwnea77w2rk8" />
      </>} data-id="element-747" data-mp="3v2kadyoi4ylg">
    <path d="M8.6 21.4L9.9 9H14.1L15.4 21.4Z" data-id="element-752" data-mp="1mooshyomascw" />
    <path d="M10.3 8.4V5.4H13.7V8.4Z" data-id="element-753" data-mp="1mpgjew79s1wu" />
    <path d="M9.8 4.9L12 2.6L14.2 4.9Z" data-id="element-754" data-mp="18x0hjyq3u9xi" />
  </Solid>;
const Worldwide: Glyph = ({
  className
}) =>
// Globe with meridians cut in
<Solid className={className} cuts={<>
        <ellipse cx="12" cy="12" rx="3.6" ry="9" fill="none" stroke="black" strokeWidth="1.1" data-id="element-756" data-mp="20o6ek6aj3rik" />
        <Cut d="M3.4 9H20.6M3.4 15H20.6M12 3V21" data-id="element-757" data-mp="25mrfk7xluf82" />
      </>} data-id="element-755" data-mp="1cz5dx57xtknu">
    <circle cx="12" cy="12" r="9.6" data-id="element-758" data-mp="25kx4rmg1s3hp" />
  </Solid>;
const destinationGlyphs: Record<string, Glyph> = {
  Dubai,
  Monaco,
  Ibiza,
  Hamburg,
  Mallorca,
  Warsaw,
  Menorca,
  Worldwide
};
export function DestinationIcon({
  city,
  className = 'h-5 w-5'
}: {
  city: string;
  className?: string;
}) {
  const G = destinationGlyphs[city] ?? Worldwide;
  return <G className={className} data-id="element-759" data-mp="l7fzdi1rzgpa" />;
}

/* ——— Concierge services ——— */

const Villa: Glyph = ({
  className
}) => <Solid className={className} cuts={<>
        <path d="M10.6 14.6H13.4V21H10.6Z" data-id="element-761" data-mp="2hx8x6tvpft8p" />
        <path d="M5.8 13.2H8.6V16H5.8Z" data-id="element-762" data-mp="2hyk1cut2b30c" />
        <path d="M15.4 13.2H18.2V16H15.4Z" data-id="element-763" data-mp="2hzlf571gq3lr" />
      </>} extra={<Stroke d="M2 21.4H22" w={1} opacity={0.5} data-id="element-764" data-mp="2d57nnfpx8gmd" />} data-id="element-760" data-mp="2mvai7kcoy2mt">
    <path d="M15.6 4.4H18V8.6L15.6 6.6Z" data-id="element-765" data-mp="2956z3293yp1l" />
    <path d="M2.6 11.2L12 3.4L21.4 11.2V20.8H2.6Z" data-id="element-766" data-mp="2945lor4mdqgp" />
  </Solid>;
const Penthouse: Glyph = ({
  className
}) => <Solid className={className} cuts={<>
        <path d="M10.2 4.6H13.8V6H10.2Z" data-id="element-768" data-mp="1z6hsi5mnwr0n" />
        <Cut d="M8.6 10.4H15.4M8.6 13.4H15.4M8.6 16.4H15.4M12 9.2V19.6" w={0.8} data-id="element-769" data-mp="1z5g5r3waag33" />
      </>} extra={<Stroke d="M3 21.6H21" w={1} opacity={0.5} data-id="element-770" data-mp="1u7y2nu4y6v30" />} data-id="element-767" data-mp="2475iqp32h3tt">
    <path d="M9.2 3.4H14.8V7H9.2Z" data-id="element-771" data-mp="1q6uqpiq0tj72" />
    <path d="M7 7.6H17V21H7Z" data-id="element-772" data-mp="1q7w1rn71e5os" />
  </Solid>;
const MotorYacht: Glyph = ({
  className
}) => <Solid className={className} cuts={<>
        <Cut d="M8.4 12.6H15.8" w={1} data-id="element-774" data-mp="3kjkmk5zy92h3" />
        <circle cx="7.6" cy="16.8" r="0.65" data-id="element-775" data-mp="3kklvo4xenew6" />
        <circle cx="10.6" cy="16.8" r="0.65" data-id="element-776" data-mp="16ckz9ocefmz0" />
        <circle cx="13.6" cy="16.8" r="0.65" data-id="element-777" data-mp="16btgje3whjq5" />
      </>} extra={<Stroke d="M2 21.6Q4.5 20.6 7 21.6T12 21.6T17 21.6T22 21.6" w={1} opacity={0.5} data-id="element-778" data-mp="1bawdeb9q8jn1" />} data-id="element-773" data-mp="3ol5udcxmmqkf">
    <path d="M11.2 5.2H12V8H11.2Z" data-id="element-779" data-mp="1g1mevdf182cg" />
    <path d="M8.8 10.6L9.6 8H13.8L15.4 10.6Z" data-id="element-780" data-mp="1g3fql74i5crb" />
    <path d="M6 14.6L7.4 10.8H15.2L18.2 14.6Z" data-id="element-781" data-mp="1k3q20zb2cvdk" />
    <path d="M2 14.8H19.2C20.6 14.8 21.6 14 22.4 13L20.4 18C20 18.8 19.4 19.2 18.6 19.2H5C3.8 19.2 2.8 18 2 14.8Z" data-id="element-782" data-mp="1k2g5iejuzv91" />
  </Solid>;
const Supercar: Glyph = ({
  className
}) =>
// Low wedge in profile, nose to the right
<Solid className={className} cuts={<>
        <path d="M10.6 12.4L12.6 10.9H14.4L17.2 12.6Z" data-id="element-784" data-mp="wdvpkiunp8tu" />
        <circle cx="6.6" cy="16.6" r="0.9" data-id="element-785" data-mp="wghyomx73we6" />
        <circle cx="17.6" cy="16.6" r="0.9" data-id="element-786" data-mp="11eb3bbiuoicc" />
        <Cut d="M19.4 13.6L21 14.1" w={0.7} data-id="element-787" data-mp="11avn25dfx39c" />
      </>} extra={<Stroke d="M0.6 13.4H2.2M0.4 15.6H1.8" w={0.9} opacity={0.6} data-id="element-788" data-mp="27siswhctwd1b" />} data-id="element-783" data-mp="sctodwh6imp8">
    <path d="M2.8 16.6L2.6 14.4L4.4 13.4L9.4 12.2L12.2 10H14.8L19.2 12.4L22 13.4L22.6 15L21.8 16.6Z" data-id="element-789" data-mp="2crvmrm24akde" />
    <circle cx="6.6" cy="16.6" r="2.4" data-id="element-790" data-mp="1of1vlao6k44h" />
    <circle cx="17.6" cy="16.6" r="2.4" data-id="element-791" data-mp="1og353apjsik3" />
  </Solid>;
const ChauffeurCap: Glyph = ({
  className
}) =>
// A chauffeur's peaked cap with badge
<Solid className={className} cuts={<>
        <circle cx="12" cy="8.6" r="1.4" data-id="element-793" data-mp="1xa9h8d3rc257" />
        <Cut d="M5.8 12.4H18.2" w={0.8} data-id="element-794" data-mp="1sgwte56wjzdi" />
        <Cut d="M6.4 14.6C8 15.6 10 16 12 16S16 15.6 17.6 14.6" w={0.7} data-id="element-795" data-mp="1sfvflsyi4ys3" />
      </>} data-id="element-792" data-mp="1x8omonhg0j7c">
    <path d="M4.6 11.2C4.6 7.4 8 4.6 12 4.6S19.4 7.4 19.4 11.2Z" data-id="element-796" data-mp="274rnnq0k6bsp" />
    <path d="M5.2 11.2H18.8V13.8H5.2Z" data-id="element-797" data-mp="227rkbmv3fn5a" />
    <path d="M5.2 13.8H18.8C18.2 16.8 15.4 18.4 12 18.4S5.8 16.8 5.2 13.8Z" data-id="element-798" data-mp="226zwwzbngvq3" />
  </Solid>;
const Cloche: Glyph = ({
  className
}) => <Solid className={className} cuts={<Cut d="M7 15.4C7.4 13 8.8 11.4 10.8 10.8" w={1.1} data-id="element-800" data-mp="2c6gxx3fyilfq" />} extra={<Stroke d="M19.4 2.8V6.2M17.7 4.5H21.1" w={1} data-id="element-801" data-mp="lsq6lznukwwm" />} data-id="element-799" data-mp="2c5fo12aog4zl">
    <path d="M4 17C4 11.6 7.6 8 12 8S20 11.6 20 17Z" data-id="element-802" data-mp="lt9s1qcpi3fe" />
    <circle cx="12" cy="6.6" r="1.5" data-id="element-803" data-mp="hrwndjlco6a2" />
    <rect x="2.4" y="17.8" width="19.2" height="1.7" rx="0.85" data-id="element-804" data-mp="hszd3o6xs2oe" />
  </Solid>;
const ChefHat: Glyph = ({
  className
}) => <Solid className={className} cuts={<>
        <Cut d="M7.6 17.2H16.4" w={0.9} data-id="element-806" data-mp="bnrp1u4xs9di" />
        <Cut d="M10 13.4V15.6M12 13.2V15.6M14 13.4V15.6" w={0.8} data-id="element-807" data-mp="bqvz38531zc3" />
      </>} data-id="element-805" data-mp="7ptyamvipf80">
    <path d="M7 13.6C4.6 13.6 3.4 11.6 3.4 9.8C3.4 7.6 5.2 6 7.2 6.2C7.8 4.2 9.8 3 12 3S16.2 4.2 16.8 6.2C18.8 6 20.6 7.6 20.6 9.8C20.6 11.6 19.4 13.6 17 13.6V20.4H7Z" data-id="element-808" data-mp="3u2skh0lnhnjs" />
  </Solid>;
const PaddockPass: Glyph = ({
  className
}) =>
// A lanyard pass with a chequered corner
<Solid className={className} cuts={<>
        <path d="M10 8.4H14V9.4H10Z" data-id="element-810" data-mp="3b4g875zfzxjz" />
        <path d="M8.2 11.8H10V13.6H8.2ZM10 13.6H11.8V15.4H10ZM8.2 15.4H10V17.2H8.2Z" data-id="element-811" data-mp="3b5hte3a6a0ff" />
        <Cut d="M13.2 12.6H15.8M13.2 15H15M8.4 19.4H15.6" w={0.8} data-id="element-812" data-mp="3g4awr2vqwb1v" />
      </>} extra={<Stroke d="M9 7L12 2.2L15 7" w={1.1} data-id="element-813" data-mp="19cjwnwd9ynp7" />} data-id="element-809" data-mp="3p4qya6sxh843">
    <rect x="6" y="6.6" width="12" height="15.2" rx="1.2" data-id="element-814" data-mp="19aqp8euu7zg5" />
  </Solid>;
const Martini: Glyph = ({
  className
}) => <Solid className={className} cuts={<circle cx="13.8" cy="7" r="1.5" data-id="element-816" data-mp="2chb9gikw0iw9" />} extra={<Stroke d="M17.6 1.8L15 5.6" w={1} data-id="element-817" data-mp="2cg8i69jnkeft" />} data-id="element-815" data-mp="1e9k162uh7ie7">
    <path d="M3.4 4H20.6L12.6 12.4V19.6H11.4V12.4Z" data-id="element-818" data-mp="2h8td5eeaoxkx" />
    <rect x="7.4" y="19.8" width="9.2" height="1.4" rx="0.7" data-id="element-819" data-mp="2h9kxtu6ctawf" />
  </Solid>;
const Flute = ({
  x,
  angle
}: {
  x: number;
  angle: number;
}) => <g transform={`translate(${x} 4) rotate(${angle})`} data-id="element-820" data-mp="27i6zvqu1wj5e">
    <path d="M-2 0H2L1.6 7.2C1.4 8.6 0.8 9.4 0 9.4S-1.4 8.6 -1.6 7.2Z" data-id="element-821" data-mp="27iq8ggxt9v6r" />
    <rect x="-0.5" y="9.2" width="1" height="7" data-id="element-822" data-mp="2v941oas4u02x" />
    <rect x="-2.4" y="16" width="4.8" height="1.1" rx="0.55" data-id="element-823" data-mp="3075mp194c9h1" />
  </g>;
const Party: Glyph = ({
  className
}) =>
// Two flutes meeting in a toast
<Solid className={className} cuts={<>
        <circle cx="8.4" cy="8" r="0.55" data-id="element-825" data-mp="2r67gqdf4drvt" />
        <circle cx="15.6" cy="8" r="0.55" data-id="element-826" data-mp="2r54pg4dvxnfd" />
      </>} extra={<Stroke d="M12 0.8V2.4M10 1.4L10.7 2.6M14 1.4L13.3 2.6" w={0.9} data-id="element-827" data-mp="1ashdf8vtr7n8" />} data-id="element-824" data-mp="2m6l342vdq9mh">
    <Flute x={9} angle={-14} data-id="element-828" data-mp="1auk8w8fqxszc" />
    <Flute x={15} angle={14} data-id="element-829" data-mp="1frtybo34y4n6" />
  </Solid>;
const Crest: Glyph = ({
  className
}) => <Solid className={className} cuts={<>
        <path d="M12 4.2L18 6.3V11C18 15.2 15.4 18 12 19.6C8.6 18 6 15.2 6 11V6.3Z" fill="none" stroke="black" strokeWidth="0.8" strokeLinejoin="round" data-id="element-831" data-mp="178gvueysswr3" />
        <circle cx="12" cy="10" r="1.7" data-id="element-832" data-mp="1h6dcfmai8jwl" />
        <path d="M11.2 11L10.6 15H13.4L12.8 11Z" data-id="element-833" data-mp="1c93h5q0fpe0u" />
      </>} data-id="element-830" data-mp="17a0ci6od99te">
    <path d="M12 2L20 4.8V11C20 16.4 16.6 19.8 12 21.8C7.4 19.8 4 16.4 4 11V4.8Z" data-id="element-834" data-mp="1c7jwzobnqits" />
  </Solid>;
const Lotus: Glyph = ({
  className
}) => <Solid className={className} cuts={<Cut d="M12 7.6V15.4" w={0.8} data-id="element-836" data-mp="jimizxehb86u" />} extra={<Stroke d="M4 20.6H20" w={1} opacity={0.5} data-id="element-837" data-mp="ya3uup0e9b7u" />} data-id="element-835" data-mp="jlh5duh1z32w">
    <path d="M12 4C14.4 6.6 15 9.6 15 12C15 15 13.6 17 12 18C10.4 17 9 15 9 12C9 9.6 9.6 6.6 12 4Z" data-id="element-838" data-mp="ybon27z8ki2j" />
    <path d="M12 18.2C7 18.2 3.4 15.8 2.4 11.4C5 11 7.4 11.8 8.6 13.4C9.2 15.4 10.4 17 12 18.2Z" data-id="element-839" data-mp="tgskwf0acebp" />
    <path d="M12 18.2C17 18.2 20.6 15.8 21.6 11.4C19 11 16.6 11.8 15.4 13.4C14.8 15.4 13.6 17 12 18.2Z" data-id="element-840" data-mp="5r7wvhp9yqvk" />
  </Solid>;
const NightBag: Glyph = ({
  className
}) =>
// Shopping bag with a crescent moon — after hours
<Solid className={className} cuts={<>
        <circle cx="11.4" cy="14.4" r="3.2" data-id="element-842" data-mp="25tzgd3nvv44d" />
        <circle cx="13" cy="13.2" r="2.7" fill="white" data-id="element-843" data-mp="2amirltz3ln60" />
      </>} extra={<Stroke d="M9 8V6.6C9 4.8 10.4 3.6 12 3.6S15 4.8 15 6.6V8" w={1.4} data-id="element-844" data-mp="2aodbd4ymv98i" />} data-id="element-841" data-mp="25vka4r2dij16">
    <path d="M5 8H19L20 21.2H4Z" data-id="element-845" data-mp="2enchuu00je22" />
  </Solid>;
const Pram: Glyph = ({
  className
}) => <Solid className={className} cuts={<>
        <Cut d="M5.4 13.6H17.2" w={0.8} data-id="element-847" data-mp="1mwervxp167ka" />
        <Cut d="M6.4 8.4L10.6 11" w={0.7} data-id="element-848" data-mp="1mx7z0zrwiz2y" />
        <circle cx="7.4" cy="19.6" r="0.55" data-id="element-849" data-mp="1qy9par3uc0ss" />
        <circle cx="15.4" cy="19.6" r="0.55" data-id="element-850" data-mp="1qxi4mbbs7nha" />
      </>} extra={<Stroke d="M18.6 11.8L19.8 7.4H21.6" w={1.2} data-id="element-851" data-mp="1vxw1qybuhd0j" />} data-id="element-846" data-mp="1hywmuie4wchk">
    <path d="M4 11A7 7 0 0 1 11 4V11Z" data-id="element-852" data-mp="1vynzofswzmtz" />
    <path d="M4 11.8H18.6C18.6 15 16 17 13 17H9.6C6.6 17 4 15 4 11.8Z" data-id="element-853" data-mp="32dotaeugvsxe" />
    <circle cx="7.4" cy="19.6" r="1.7" data-id="element-854" data-mp="32fsx6wsd8rxo" />
    <circle cx="15.4" cy="19.6" r="1.7" data-id="element-855" data-mp="37bqdx47jk0az" />
  </Solid>;
const Bell: Glyph = ({
  className
}) => <Solid className={className} cuts={<Cut d="M6.6 15.4C6.9 13.1 8.4 11.3 10.4 10.7" w={1.1} data-id="element-857" data-mp="3hfyi290mwi56" />} data-id="element-856" data-mp="3hgpzmfxecfcg">
    <path d="M3.6 17C3.6 12 7.4 8.6 12 8.6S20.4 12 20.4 17Z" data-id="element-858" data-mp="3ch3ms7lhd146" />
    <rect x="11.2" y="6" width="1.6" height="2.8" data-id="element-859" data-mp="3cjpv49g73kng" />
    <circle cx="12" cy="5.4" r="1.4" data-id="element-860" data-mp="37ploxser7gnf" />
    <rect x="2" y="17.8" width="20" height="1.8" rx="0.9" data-id="element-861" data-mp="37okd3lpwyq4n" />
  </Solid>;
const HomeKey: Glyph = ({
  className
}) =>
// A key with a house cut into its bow
<Solid className={className} cuts={<path d="M4.8 12.8L7 10.6L9.2 12.8V14.6H4.8Z" data-id="element-863" data-mp="2ynvuedpks1lf" />} data-id="element-862" data-mp="2yr04frpq1rk0">
    <circle cx="7" cy="12.4" r="4.8" data-id="element-864" data-mp="2tqm6x3lqy008" />
    <rect x="11" y="11.4" width="11" height="2" data-id="element-865" data-mp="2tpklq6b0nx4s" />
    <path d="M17.2 13.4H19.2V16.4H17.2Z" data-id="element-866" data-mp="2or9em3c3w0q9" />
    <path d="M20 13.4H21.8V15.6H20Z" data-id="element-867" data-mp="2os2mx8qpqyai" />
  </Solid>;
const Sparkle: Glyph = ({
  className
}) => <Solid className={className} data-id="element-868" data-mp="2krs8d7ouuzk1">
    <path d="M11 2C11.8 7.6 13.4 9.2 19 10C13.4 10.8 11.8 12.4 11 18C10.2 12.4 8.6 10.8 3 10C8.6 9.2 10.2 7.6 11 2Z" data-id="element-869" data-mp="n0fq2qk54964" />
    <path d="M18.4 14.6C18.7 16.6 19.2 17.1 21.2 17.4C19.2 17.7 18.7 18.2 18.4 20.2C18.1 18.2 17.6 17.7 15.6 17.4C17.6 17.1 18.1 16.6 18.4 14.6Z" data-id="element-870" data-mp="iyb1454n00pt" />
  </Solid>;
const serviceGlyphs: Record<string, Glyph> = {
  'Staffed villa': Villa,
  'Penthouse suite': Penthouse,
  'Yacht with crew': MotorYacht,
  'Supercar delivered': Supercar,
  'Chauffeur on call': ChauffeurCap,
  'Impossible tables': Cloche,
  'Private chef': ChefHat,
  'Paddock passes': PaddockPass,
  'Club table': Martini,
  'Private party': Party,
  'Discreet security': Crest,
  'Spa, closed for you': Lotus,
  'Boutique after hours': NightBag,
  'Trusted nanny': Pram,
  '24/7 assistant': Bell,
  'Off-market home': HomeKey,
  'Something unusual': Sparkle
};
export function ServiceIcon({
  service,
  className = 'h-[18px] w-[18px]'
}: {
  service: string;
  className?: string;
}) {
  const G = serviceGlyphs[service] ?? Sparkle;
  return <G className={className} data-id="element-871" data-mp="3khjf0o171brq" />;
}

/* ——— Transport ——— */

const Jet: Glyph = ({
  className
}) =>
// Business jet from above, climbing away
<Solid className={className} cuts={<Cut d="M12 3.4V5" w={0.8} data-id="element-873" data-mp="35vclwbnbit6z" />} data-id="element-872" data-mp="3pgmm0e0qhs2c">
    <g transform="rotate(45 12 12)" data-id="element-874" data-mp="35ukrh45gj1ia">
      <path d="M12 1.6C12.9 1.6 13.3 3 13.3 4.6V9.4L21.6 14V15.6L13.3 13V18.2L16 20.2V21.6L12 20.6L8 21.6V20.2L10.7 18.2V13L2.4 15.6V14L10.7 9.4V4.6C10.7 3 11.1 1.6 12 1.6Z" data-id="element-875" data-mp="35vmca0c9z2d7" />
      <rect x="9" y="14.2" width="1.4" height="3.2" rx="0.7" data-id="element-876" data-mp="3ewtznnd96isi" />
      <rect x="13.6" y="14.2" width="1.4" height="3.2" rx="0.7" data-id="element-877" data-mp="3aup6squmpq6x" />
    </g>
  </Solid>;
const Helicopter: Glyph = ({
  className
}) => <Solid className={className} cuts={<path d="M16 9C18 9.4 19.4 10.8 19.8 12.6H16Z" data-id="element-879" data-mp="3orvn8ervrcsz" />} extra={<Stroke d="M9 19H20.4M11.2 15.4V19M17.6 15.4V19" w={1.2} data-id="element-880" data-mp="3jucbpmmkhe4q" />} data-id="element-878" data-mp="3osxbxm1tjxt6">
    <rect x="3.6" y="4.4" width="16.8" height="1.2" rx="0.6" data-id="element-881" data-mp="2f5go0p98nw3" />
    <rect x="13.2" y="5.4" width="1.2" height="2.8" data-id="element-882" data-mp="2gqga4qfex0t" />
    <path d="M7 12.6C7 9.8 9.2 8 12 8H14.4C17.8 8 20.4 10.4 20.4 13.6V15.4H9.8C8.2 15.4 7 14.2 7 12.6Z" data-id="element-883" data-mp="3tlzvd1cvtqvn" />
    <rect x="2" y="10.4" width="5.6" height="1.2" data-id="element-884" data-mp="bg3oku2tdfjy" />
    <rect x="2" y="8.4" width="1.3" height="3.2" data-id="element-885" data-mp="6g8yuturyw0h" />
  </Solid>;
const Concierge: Glyph = ({
  className
}) =>
// The concierge: jacket, open collar and bow tie
<Solid className={className} cuts={<>
        <path d="M10.2 12.6L12 18.4L13.8 12.6Z" data-id="element-887" data-mp="356s9zbfdkm9w" />
        <Cut d="M9.2 13.2L8.2 17.4L10.6 18M14.8 13.2L15.8 17.4L13.4 18" w={0.6} data-id="element-888" data-mp="357jwzxuwpbok" />
      </>} extra={<path d="M10.3 13.4L12 14.2L13.7 13.4V15.2L12 14.4L10.3 15.2Z" fill="currentColor" data-id="element-889" data-mp="396smurmtspeu" />} data-id="element-886" data-mp="309s3x0iizjis">
    <circle cx="12" cy="6.6" r="3.6" data-id="element-890" data-mp="3e0wwxjre1dk5" />
    <path d="M4 21.4C4 15.6 7.6 12.4 12 12.4S20 15.6 20 21.4Z" data-id="element-891" data-mp="3iyynsqv22h66" />
  </Solid>;
const Footsteps: Glyph = ({
  className
}) => <Solid className={className} data-id="element-892" data-mp="3nwqghn3iy7bf">
    <ellipse cx="8.2" cy="13.8" rx="2.5" ry="3.7" transform="rotate(-8 8.2 13.8)" data-id="element-893" data-mp="3nvyladduablo" />
    <ellipse cx="8.8" cy="19.9" rx="1.8" ry="1.6" data-id="element-894" data-mp="3sy7cp5b2d5lo" />
    <ellipse cx="15.8" cy="6.6" rx="2.5" ry="3.7" transform="rotate(8 15.8 6.6)" data-id="element-895" data-mp="stt7wnbtg4i" />
    <ellipse cx="15.2" cy="12.7" rx="1.8" ry="1.6" data-id="element-896" data-mp="qr3zetzzr0u" />
  </Solid>;
const transportGlyphs: Record<string, Glyph[]> = {
  'Private jet': [Jet],
  Chauffeur: [ChauffeurCap],
  'Self-drive car': [Supercar],
  Yacht: [MotorYacht],
  Helicopter: [Helicopter],
  'Jet + chauffeur': [Jet, ChauffeurCap],
  'Jet + yacht': [Jet, MotorYacht],
  'Helicopter + yacht': [Helicopter, MotorYacht],
  'Let Superluxury plan it': [Concierge],
  "I'll get there myself": [Footsteps]
};

/* ——— Membership privileges ——— */

const privilegeGlyphs: Record<string, Glyph> = {
  'Homes never listed': Villa,
  'Wheels up in four hours': Jet,
  'Nights never photographed': Party,
  'Retreats for one household': Lotus
};
export function PrivilegeIcon({
  title,
  className = 'h-5 w-5'
}: {
  title: string;
  className?: string;
}) {
  const G = privilegeGlyphs[title] ?? Sparkle;
  return <G className={className} data-id="element-897" data-mp="24xsi6wy7ezix" />;
}

// One icon, or a pair for combined journeys
export function TransportIcon({
  option
}: {
  option: string;
}) {
  const glyphs = transportGlyphs[option] ?? [Concierge];
  return <span className="flex items-center gap-0.5" data-id="element-898" data-mp="tljab8emj9jj">
      {glyphs.map((G, i) => <G key={i} className={glyphs.length > 1 ? 'h-4 w-4' : 'h-[18px] w-[18px]'} data-id="element-899" data-mp="ok2dbnz9g788" data-mpi={i} />)}
    </span>;
}